/**
 * The operation log's action codes (`03-multitenancy.md` is silent on them;
 * they exist because the log itself does).
 *
 * Unlike every other catalog here, this one does not gate anything. `action`
 * on `writeOperationLog(...)` is a free-form string written at each call
 * site — never a `pgEnum`, never checked against a list before the write —
 * so `enum-messages.test.ts`'s opt-in-by-prefix scan cannot see it, and a
 * merchant reading the log in Khmer saw the database's own English word
 * (`staff.create`) printed next to a translated column header.
 *
 * This is the set as it is actually written, found by reading every
 * `writeOperationLog` call under `apps/api/src/routes/**`. **Nothing here is
 * renamed** — a code is a row already written, in a table with no editor and
 * no delete — so `reservations.ts`'s bare `"create"`, `"seat"`, `"no_show"`
 * and `"cancel"` stay bare rather than gaining a prefix that would make them
 * read like the rest.
 */

import type { MessageKey } from "../i18n/messages/en.ts";

type OperationLogActionDefinition = {
  readonly key: string;
  readonly name: string;
  /** No route writes this any more; it stays so old rows still translate. */
  readonly retired?: true;
};

const DEFINITIONS = [
  { key: "billing.renew", name: "Subscription renewed" },
  { key: "campaign.create", name: "Campaign created" },
  { key: "campaign.delete", name: "Campaign deleted" },
  { key: "campaign.update", name: "Campaign updated" },
  { key: "cancel", name: "Booking cancelled" },
  { key: "chain.capabilities_changed", name: "Store capabilities changed" },
  { key: "chain.menu_pushed", name: "Menu pushed to stores" },
  { key: "chain.requisition_approved", name: "Requisition approved" },
  { key: "chain.requisition_rejected", name: "Requisition rejected" },
  { key: "chain.store_added", name: "Store added to chain" },
  { key: "chain.user_access_changed", name: "Store access changed" },
  { key: "cost.create", name: "Cost added" },
  { key: "cost.delete", name: "Cost deleted" },
  { key: "cost.update", name: "Cost updated" },
  { key: "coupon.issue", name: "Coupon issued" },
  { key: "coupon_template.copy", name: "Coupon template copied" },
  { key: "coupon_template.create", name: "Coupon template created" },
  { key: "coupon_template.update", name: "Coupon template updated" },
  { key: "create", name: "Booking created" },
  { key: "gift_card.issue", name: "Gift card issued" },
  { key: "gift_card.top_up", name: "Gift card topped up" },
  { key: "gift_card.void", name: "Gift card voided" },
  { key: "inventory.inbound_approved", name: "Inbound ticket approved" },
  { key: "inventory.inbound_rejected", name: "Inbound ticket rejected" },
  { key: "inventory.outbound_issued", name: "Outbound order issued" },
  { key: "inventory.purchase_notified", name: "Purchase order sent to supplier" },
  { key: "inventory.purchase_raised", name: "Purchase order raised" },
  { key: "inventory.purchase_received", name: "Purchase order received" },
  { key: "inventory.purchase_voided", name: "Purchase order voided" },
  { key: "inventory.recipe_changed", name: "Item recipe changed" },
  { key: "inventory.stock_adjusted", name: "Stock adjusted" },
  { key: "inventory.supplier_prices_changed", name: "Supplier prices changed" },
  { key: "inventory.transfer_accepted", name: "Transfer accepted" },
  { key: "inventory.transfer_rejected", name: "Transfer rejected" },
  { key: "item.batch", name: "Items updated in bulk" },
  { key: "item.create", name: "Item added" },
  { key: "item.delete", name: "Item deleted" },
  { key: "item.meal_set_changed", name: "Meal set changed" },
  { key: "item.update", name: "Item updated" },
  { key: "job.create", name: "Job added" },
  { key: "job.delete", name: "Job deleted" },
  { key: "job.update", name: "Job updated" },
  { key: "no_show", name: "Booking marked no-show" },
  { key: "notifications.update", name: "Alert settings updated" },
  { key: "payment_method.create", name: "Payment method added" },
  { key: "payment_method.delete", name: "Payment method deleted" },
  { key: "print.reprint", name: "Receipt reprinted" },
  { key: "printer.bind", name: "Printer assigned" },
  { key: "printer.create", name: "Printer added" },
  { key: "printer.unbind", name: "Printer unassigned" },
  { key: "promotion.create", name: "Promotion created" },
  { key: "promotion.delete", name: "Promotion deleted" },
  { key: "promotion.update", name: "Promotion updated" },
  { key: "receipt_template.update", name: "Receipt template updated" },
  { key: "role.create", name: "Role created" },
  { key: "role.update", name: "Role updated" },
  { key: "royalty.run", name: "Royalty charges run" },
  { key: "royalty_charge.finalise", name: "Royalty charge finalised" },
  { key: "royalty_scheme.create", name: "Royalty scheme created" },
  { key: "royalty_scheme.set_stores", name: "Royalty scheme stores changed" },
  { key: "royalty_scheme.update", name: "Royalty scheme updated" },
  { key: "seat", name: "Party seated" },
  { key: "service_charge_rate.create", name: "Service charge rate added" },
  { key: "service_charge_rate.delete", name: "Service charge rate deleted" },
  { key: "service_charge_rate.update", name: "Service charge rate updated" },
  { key: "settings.delivery_providers_changed", name: "Delivery providers changed" },
  { key: "settings.invoice_changed", name: "Invoice numbering changed" },
  { key: "settlement.drawer_opened", name: "Cash drawer opened" },
  { key: "shift.close", name: "Shift closed" },
  { key: "specification_group.create", name: "Specification group created" },
  { key: "specification_group.delete", name: "Specification group deleted" },
  { key: "specification_group.update", name: "Specification group updated" },
  { key: "staff.create", name: "Employee added" },
  { key: "staff.delete", name: "Employee deleted" },
  { key: "staff.photo_delete", name: "Employee photo removed" },
  { key: "staff.photo_set", name: "Employee photo set" },
  { key: "staff.reset_pin", name: "Employee PIN reset", retired: true },
  { key: "staff.take_till", name: "Took a till by scanning" },
  { key: "staff.assign_till", name: "Put on a till by a manager" },
  { key: "staff.update", name: "Employee updated" },
  { key: "staff.set_password", name: "Employee password set" },
  { key: "store_group.create", name: "Store group created" },
  { key: "store_group.delete", name: "Store group deleted" },
  { key: "store_group.set_stores", name: "Store group stores changed" },
  { key: "store_group.update", name: "Store group updated" },
  { key: "store_profile.update", name: "Store profile updated" },
  { key: "table_fee.update", name: "Table fee updated" },
  { key: "tax_rate.create", name: "Tax rate added" },
  { key: "tax_rate.delete", name: "Tax rate deleted" },
  { key: "tax_rate.update", name: "Tax rate updated" },
  { key: "tax_settings.update", name: "Tax settings updated" },
  { key: "time_entry.correct", name: "Timesheet corrected" },
  { key: "tip_distribution.finalise", name: "Tip distribution finalised" },
  { key: "tip_pool.create", name: "Tip pool created" },
  { key: "tip_pool.delete", name: "Tip pool deleted" },
  { key: "tip_pool.run", name: "Tip pool run" },
  { key: "tip_pool.set_jobs", name: "Tip pool jobs changed" },
  { key: "tip_pool.update", name: "Tip pool updated" },
  { key: "tip_settings.update", name: "Tip settings updated" },
  { key: "topping_template.create", name: "Topping template created" },
  { key: "topping_template.delete", name: "Topping template deleted" },
  { key: "topping_template.update", name: "Topping template updated" },
  { key: "topup_tier.create", name: "Top-up tier created" },
  { key: "topup_tier.delete", name: "Top-up tier deleted" },
  { key: "trading_config.update", name: "Trading hours updated" },
] as const satisfies readonly OperationLogActionDefinition[];

export type OperationLogAction = (typeof DEFINITIONS)[number]["key"];

export const OPERATION_LOG_ACTION_CATALOG: readonly OperationLogActionDefinition[] = DEFINITIONS;

export const OPERATION_LOG_ACTIONS = DEFINITIONS.map((a) => a.key) as unknown as readonly [
  OperationLogAction,
  ...OperationLogAction[],
];

const BY_KEY = new Set<string>(OPERATION_LOG_ACTIONS);

export function isOperationLogAction(value: string): value is OperationLogAction {
  return BY_KEY.has(value);
}

/**
 * The message key an action's label renders under.
 *
 * Not `action.<key>` — that namespace already holds ~20 generic UI verbs
 * (`action.cancel` is the Cancel button), and `reservations.ts` writes the
 * bare action `"cancel"`. Reusing it would have collided the audit phrase
 * with the button label under one key, silently repointing every Cancel
 * button in the product the day this catalog's entries were added, and
 * `en.ts` would fail to compile besides — TypeScript rejects an object
 * literal with the same property twice. `operationAction.` is its own
 * namespace for the same reason `permission.` is: see `permissionMessageKey`
 * in `permissions.ts`.
 */
export function operationLogActionMessageKey(key: OperationLogAction): MessageKey {
  return `operationAction.${key}` as MessageKey;
}
